import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  UsageManagementService,
  CreateInviteRequest,
} from "@/lib/services/usage-management";
import { ResendService } from "@/lib/services/resend-service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Copy,
  Share2,
  BarChart3,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Mail,
  Link,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabase";

interface FriendInviteStats {
  totalInvites: number;
  acceptedInvites: number;
  totalUsage: number;
  totalCost: number;
}

export function FriendInviteManager() {
  const { user } = useAuth();
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showInviteDetails, setShowInviteDetails] = useState(false);
  const [inviteDetailsList, setInviteDetailsList] = useState<
    {
      invite_code: string;
      friend_email: string;
      friend_name: string | null;
      status: string;
      created_at: string;
      accepted_at: string | null;
    }[]
  >([]);
  const [stats, setStats] = useState<FriendInviteStats>({
    totalInvites: 0,
    acceptedInvites: 0,
    totalUsage: 0,
    totalCost: 0,
  });
  const [inviteForm, setInviteForm] = useState<CreateInviteRequest>({
    friend_email: "",
    friend_name: "",
    daily_limit: 5,
    trial_days: 3,
  });
  const [createdInvite, setCreatedInvite] = useState<{
    invite_code: string;
    share_url: string;
    expires_at: string;
  } | null>(null);
  const [emailStatus, setEmailStatus] = useState<{
    isValid: boolean;
    isAvailable: boolean;
    message: string;
  }>({
    isValid: false,
    isAvailable: false,
    message: "",
  });

  const loadStats = useCallback(async () => {
    if (!user) return;

    try {
      // Loading stats for user
      const stats = await UsageManagementService.getFriendTestingStats(user.id);
      // Stats loaded successfully
      setStats(stats);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadStats();
      // Check backend health on component mount
      ResendService.checkBackendHealth().then((health) => {
        if (!health.success) {
          console.warn("Backend health check failed:", health.error);
          toast.warning("El servicio de email puede no estar disponible");
        }
      });
    }
  }, [user, loadStats]);

  const checkEmailAvailability = async (email: string) => {
    if (!email) return;

    try {
      // Use a simpler query to avoid the 406 error
      const { data, error } = await supabase
        .from("friend_testing_invites")
        .select("id")
        .eq("friend_email", email)
        .maybeSingle();

      if (error) {
        console.error("Error checking email availability:", error);
        return;
      }

      const isAvailable = !data;
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      setEmailStatus({
        isValid,
        isAvailable,
        message: isAvailable
          ? "Email disponible"
          : "Este email ya ha sido invitado",
      });
    } catch (error) {
      console.error("Error checking email availability:", error);
    }
  };

  const handleCreateInvite = async () => {
    if (!user) return;

    setIsCreatingInvite(true);
    try {
      const invite = await UsageManagementService.createFriendInvite(
        user.id,
        inviteForm
      );

      // Try to send the invite email via Resend service
      setIsSendingEmail(true);
      try {
        const emailResult = await ResendService.sendFriendInviteEmail(
          inviteForm.friend_email,
          {
            friendName: inviteForm.friend_name || inviteForm.friend_email,
            inviterName:
              user.user_metadata?.full_name || user.email || "Un usuario",
            inviteCode: invite.invite_code,
            shareUrl: invite.share_url,
            dailyLimit: inviteForm.daily_limit,
            trialDays: inviteForm.trial_days,
            expiresAt: invite.expires_at,
          }
        );

        if (!emailResult.success) {
          if (emailResult.error.includes("CORS error")) {
            toast.warning(
              "Invitación creada exitosamente. El email no se pudo enviar automáticamente debido a restricciones técnicas. Puedes copiar y enviar manualmente la información de la invitación."
            );
            setEmailSentSuccess(false);
          } else {
            toast.error(
              "Invitación creada pero no se pudo enviar el email: " +
                emailResult.error
            );
            setEmailSentSuccess(false);
          }
          console.error("Email sending failed:", emailResult.error);
        } else {
          toast.success("Invitación creada y email enviado exitosamente");
          setEmailSentSuccess(true);
        }
      } catch (error) {
        console.error("Error sending email:", error);
        toast.warning(
          "Invitación creada exitosamente. El email no se pudo enviar automáticamente. Puedes copiar y enviar manualmente la información de la invitación."
        );
        setEmailSentSuccess(false);
      } finally {
        setIsSendingEmail(false);
      }
      setCreatedInvite(invite);
      setShowInviteDialog(false);
      setInviteForm({
        friend_email: "",
        friend_name: "",
        daily_limit: 5,
        trial_days: 3,
      });
      toast.success("Invitación creada exitosamente");
      loadStats(); // Refresh stats
    } catch (error) {
      console.error("Error creating invite:", error);

      // Handle specific database constraint errors
      if (error instanceof Error) {
        if (error.message.includes("unique_friend_email")) {
          toast.error("Este email ya ha sido invitado anteriormente");
        } else if (
          error.message.includes("unique_user_id") ||
          error.message.includes("23505")
        ) {
          toast.error(
            "Ya tienes una invitación activa. No puedes crear múltiples invitaciones simultáneamente."
          );
        } else {
          toast.error(error.message);
        }
      } else {
        // Check if it's an API response error with database constraint details
        const errorObj = error as any;
        if (
          errorObj?.code === "23505" ||
          errorObj?.message?.includes("unique_user_id")
        ) {
          toast.error(
            "Ya tienes una invitación activa. No puedes crear múltiples invitaciones simultáneamente."
          );
        } else {
          toast.error("Error al crear la invitación");
        }
      }
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado al portapapeles");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Error al copiar");
    }
  };

  const shareInvite = async (url: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Invitación para probar MiAsistenteLegalIA",
          text: "Te invito a probar mi asistente legal con IA. Usa este enlace para acceder:",
          url: url,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback to copying URL
      copyToClipboard(url);
    }
  };

  const sendEmailInvite = async (email: string, url: string) => {
    try {
      setIsSendingEmail(true);
      const emailResult = await ResendService.sendFriendInviteEmail(email, {
        friendName: inviteForm.friend_name || email.split("@")[0], // Use friend name or email prefix
        inviterName:
          user?.user_metadata?.full_name || user?.email || "Un usuario",
        inviteCode: createdInvite?.invite_code || "SHARE",
        shareUrl: url,
        dailyLimit: inviteForm.daily_limit,
        trialDays: inviteForm.trial_days,
        expiresAt:
          createdInvite?.expires_at ||
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      });

      if (emailResult.success) {
        toast.success("✅ Email de invitación enviado exitosamente");
        setEmailSentSuccess(true);
      } else {
        toast.error("❌ Error enviando email: " + emailResult.error);
        setEmailSentSuccess(false);
      }
    } catch (error) {
      console.error("Error sending email invite:", error);
      toast.error("❌ Error al enviar email de invitación");
      setEmailSentSuccess(false);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const fetchInviteDetails = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("friend_testing_invites")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching invite details:", error);
        return;
      }

      setInviteDetailsList(data || []);
    } catch (error) {
      console.error("Error fetching invite details:", error);
    }
  };

  const handleRemoveRecentSearch = async (searchId: string) => {
    // Implementation for removing individual searches
  };

  const handleClearAllRecentSearches = async () => {
    // Implementation for clearing all searches
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Invitar Amigos
          </h2>
          <p className="text-muted-foreground">
            Comparte tu asistente legal con amigos y controla su uso
          </p>
        </div>
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Invitar Amigo
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invitar Amigo</DialogTitle>
              <DialogDescription>
                Crea una invitación personalizada para que tu amigo pruebe el
                asistente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="friend_email">Email del amigo *</Label>
                <Input
                  id="friend_email"
                  type="email"
                  placeholder="amigo@ejemplo.com"
                  value={inviteForm.friend_email}
                  onChange={(e) => {
                    const email = e.target.value;
                    setInviteForm((prev) => ({
                      ...prev,
                      friend_email: email,
                    }));

                    // Reset status when email changes
                    setEmailStatus({
                      isValid: false,
                      isAvailable: false,
                      message: "",
                    });

                    // Check availability after a short delay
                    if (email) {
                      setTimeout(() => checkEmailAvailability(email), 500);
                    }
                  }}
                  className={`${
                    emailStatus.isValid
                      ? emailStatus.isAvailable
                        ? "border-green-200 focus:border-green-500"
                        : "border-red-200 focus:border-red-500"
                      : ""
                  }`}
                />
                {emailStatus.message && (
                  <p
                    className={`text-xs mt-1 ${
                      emailStatus.isAvailable
                        ? "text-green-600"
                        : "text-red-600"
                    }`}>
                    {emailStatus.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="friend_name">Nombre (opcional)</Label>
                <Input
                  id="friend_name"
                  placeholder="Nombre del amigo"
                  value={inviteForm.friend_name}
                  onChange={(e) =>
                    setInviteForm((prev) => ({
                      ...prev,
                      friend_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="daily_limit">Límite diario</Label>
                  <Select
                    value={inviteForm.daily_limit?.toString()}
                    onValueChange={(value) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        daily_limit: parseInt(value),
                      }))
                    }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 requests</SelectItem>
                      <SelectItem value="5">5 requests</SelectItem>
                      <SelectItem value="10">10 requests</SelectItem>
                      <SelectItem value="15">15 requests</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="trial_days">Días de prueba</Label>
                  <Select
                    value={inviteForm.trial_days?.toString()}
                    onValueChange={(value) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        trial_days: parseInt(value),
                      }))
                    }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 día</SelectItem>
                      <SelectItem value="3">3 días</SelectItem>
                      <SelectItem value="7">7 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCreateInvite}
                  disabled={
                    isCreatingInvite ||
                    isSendingEmail ||
                    !inviteForm.friend_email ||
                    !emailStatus.isAvailable
                  }
                  className="flex-1">
                  {isCreatingInvite
                    ? "Creando..."
                    : isSendingEmail
                    ? "Procesando..."
                    : "Crear Invitación"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-lg transition"
          onClick={() => {
            setShowInviteDetails(true);
            fetchInviteDetails();
          }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Invitaciones
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalInvites}</div>
            <p className="text-xs text-muted-foreground">
              Invitaciones enviadas
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aceptadas</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.acceptedInvites}</div>
            <p className="text-xs text-muted-foreground">
              Invitaciones aceptadas
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Uso</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsage}</div>
            <p className="text-xs text-muted-foreground">Requests realizados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Costo Estimado
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${stats.totalCost.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Costo total estimado
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Created Invite Display */}
      {createdInvite && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              Invitación Creada
              {emailSentSuccess && (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800 border-green-300">
                  <Mail className="h-3 w-3 mr-1" />
                  Email Enviado
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Código de Invitación
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={createdInvite.invite_code}
                  readOnly
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(createdInvite.invite_code)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Enlace de Compartir</Label>
              <div className="flex items-center gap-2">
                <Input value={createdInvite.share_url} readOnly />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(createdInvite.share_url)}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shareInvite(createdInvite.share_url)}>
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    sendEmailInvite(
                      inviteForm.friend_email,
                      createdInvite.share_url
                    )
                  }>
                  <Mail className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Expira: {new Date(createdInvite.expires_at).toLocaleDateString()}
            </div>
            {emailSentSuccess && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-100 p-2 rounded">
                <Mail className="h-4 w-4" />
                Email de invitación enviado a {inviteForm.friend_email}
              </div>
            )}
            {!emailSentSuccess && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-100 p-2 rounded border border-amber-200">
                <AlertTriangle className="h-4 w-4" />
                <div>
                  <p className="font-medium">
                    Email no enviado automáticamente
                  </p>
                  <p className="text-xs">
                    Copia la información de arriba y envía manualmente la
                    invitación a tu amigo.
                  </p>
                </div>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => setCreatedInvite(null)}
              className="w-full">
              Cerrar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Information Alert */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Información importante:</strong> Los amigos que acepten tu
          invitación tendrán acceso limitado al asistente durante el período de
          prueba. Puedes controlar cuántas requests pueden hacer por día y por
          cuánto tiempo tienen acceso.
        </AlertDescription>
      </Alert>

      {/* Invite Details Dialog */}
      <Dialog open={showInviteDetails} onOpenChange={setShowInviteDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalles de Invitaciones</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left">Email</th>
                  <th className="px-2 py-1 text-left">Nombre</th>
                  <th className="px-2 py-1 text-left">Estado</th>
                  <th className="px-2 py-1 text-left">Fecha Enviada</th>
                  <th className="px-2 py-1 text-left">Fecha Aceptada</th>
                </tr>
              </thead>
              <tbody>
                {inviteDetailsList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-4 text-muted-foreground">
                      No hay invitaciones registradas.
                    </td>
                  </tr>
                ) : (
                  inviteDetailsList.map((invite) => (
                    <tr key={invite.invite_code}>
                      <td className="px-2 py-1">{invite.friend_email}</td>
                      <td className="px-2 py-1">{invite.friend_name || "-"}</td>
                      <td className="px-2 py-1">{invite.status}</td>
                      <td className="px-2 py-1">
                        {invite.created_at
                          ? new Date(invite.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-2 py-1">
                        {invite.accepted_at
                          ? new Date(invite.accepted_at).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
